import React, { Fragment } from "react";
import { QuestionProps } from "../../survey";
import _ from "lodash";
import SingleSelect from "./single_select";
import MultiSelect from "./multi_select";
import Input from "./input";

// Answer can take on any shape.
export interface Answer {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [propName: string]: any;
}

export interface FollowUp {
  whenOidSelectedOr?: string[];
  qid: string;
  text: string;
  description?: string;
  optional?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  qInfo: any;
}

export default function Generic({
  customizations,
  currentAnswer,
  updateAnswer,
  setAbleToSubmit,
  showValidation,
  showRefreshedComponents = true,
}: QuestionProps<
  Answer,
  {
    qid: string;
    text: string;
    description?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    qInfo: any;
    followUps?: FollowUp[];
  }
>) {
  const qid = customizations.qid;
  const followUps = customizations.followUps || [];

  // With the new answer, remove any stale follow up answers.
  const updateAndScrubAnswer = (answer: Answer) => {
    // Quickly get what we would be showing
    const tempNewFollowUps = filterFollowUpConditions(qid, answer, followUps);

    // Queue up all irrelevant follow ups to remove
    const followUpsToRemove = followUps.filter((followUp) => {
      return !tempNewFollowUps.map((f) => f.qid).includes(followUp.qid);
    });

    // Remove the irrelevant follow ups
    const newAnswers = _.omit(
      currentAnswer,
      followUpsToRemove.map((followUp) => followUp.qid),
    );
    updateAnswer({ ...newAnswers, ...answer });
  };

  const followUpsToDisplay = filterFollowUpConditions(qid, currentAnswer, followUps);
  const QuestionComponent = componentForQuestionType(customizations.qInfo.type);

  return (
    <Fragment>
      <QuestionComponent
        key={qid}
        customizations={customizations}
        currentAnswer={currentAnswer}
        updateAnswer={updateAndScrubAnswer}
        setAbleToSubmit={setAbleToSubmit}
        questionId={qid}
        showValidation={showValidation}
        showRefreshedComponents={showRefreshedComponents}
      />

      {/* TODO: There is a bug here where only the last of the follow-ups will dictate setAbleToSubmit, not addressing this yet since we might overhaul this component */}
      {followUpsToDisplay.map((followUp) => (
        <Generic
          key={followUp.qid}
          customizations={followUp}
          currentAnswer={currentAnswer}
          updateAnswer={updateAndScrubAnswer}
          setAbleToSubmit={setAbleToSubmit}
          questionId={qid}
          showValidation={showValidation}
          showRefreshedComponents={showRefreshedComponents}
        />
      ))}
    </Fragment>
  );
}

function componentForQuestionType(type: string) {
  switch (type) {
    case "single_select":
      return SingleSelect;
    case "multi_select":
      return MultiSelect;
    case "short_text": // can't be renamed without changing database values
      return Input;
  }
  // Not implemented yet
  throw `Unsupported generic question type: ${type}.`;
}

function filterFollowUpConditions(
  qid: string,
  currentAnswer?: Answer,
  followUps?: FollowUp[],
): FollowUp[] {
  if (!currentAnswer) {
    return [];
  }
  if (followUps) {
    return followUps.filter((followUp) => {
      const answerArray = Array.isArray(currentAnswer[qid])
        ? currentAnswer[qid]
        : Array(currentAnswer[qid]);
      if (
        followUp.whenOidSelectedOr &&
        _.intersection(followUp.whenOidSelectedOr, answerArray).length > 0
      ) {
        return true;
      }
    });
  }
  return [];
}
